import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CartProvider } from "@/features/cart";
import { ShopLayout } from "./components/ShopLayout";
import { ShopHero, SHOP_PRODUCTS_ID } from "./components/ShopHero";
import { ShopFilters } from "./components/ShopFilters";
import { ProductGrid } from "./components/ProductGrid";
import { ProductGridSkeleton } from "./components/ProductGridSkeleton";
import { ShopEmpty } from "./components/ShopEmpty";
import { ShopError } from "./components/ShopError";
import { ShopCta } from "./components/ShopCta";
import { ContactSection } from "./components/ContactSection";
import { useShopCurrency } from "./useShopCurrency";
import { useShopProducts } from "./useShopProducts";
import {
  categoriesOf,
  filterByCategory,
  localeOf,
  sortProducts,
  type SortValue,
} from "./shop.catalog";

/**
 * La tienda pública.
 *
 * ── Qué hace esta pantalla, y qué no ─────────────────────────────────
 * Compone. Cada pieza —portada, filtros, rejilla, cierre, contacto— es un
 * componente con su propio archivo; aquí solo vive el ESTADO que dos de
 * ellas comparten (la categoría, el orden, la moneda) y la decisión de
 * qué se pinta según cómo venga la petición.
 *
 * ── Filtrar y ordenar ocurre en el navegador ─────────────────────────
 * El contrato acepta `category` y `sort` como parámetros del servidor,
 * pero el catálogo entero cabe en una petición: hacerlo aquí significa
 * que pulsar un chip es instantáneo y no un viaje a la red que puede
 * fallar. El día que el catálogo no quepa, esto se mueve al servidor y la
 * pantalla apenas cambia — por eso las funciones son puras y viven fuera.
 *
 * ── Los tres estados de la petición se ven distintos ─────────────────
 * Cargando → esqueletos con la forma de las tarjetas. Error → un aviso
 * con su código y un botón de reintentar. Vacío → un estado que NO parece
 * un error, porque no lo es. Nunca una pantalla en blanco.
 */
export function ShopPage() {
  const { i18n } = useTranslation();
  const locale = localeOf(i18n.language);

  const [currency, setCurrency] = useShopCurrency();
  const [category, setCategory] = useState<string | null>(null);
  const [sort, setSort] = useState<SortValue>("recommended");

  const { data, isPending, isError, error, refetch, isFetching } = useShopProducts();

  // Las membresías viven en /paquetes, no entre los productos de la tienda.
  const products = useMemo(() => (data?.items ?? []).filter((p) => p.type !== "membership"), [data]);

  /*
   * Las categorías salen del servidor cuando vienen (`data.categories`),
   * y si no, se deducen del catálogo recibido. Deducirlas siempre sería
   * un error sutil: la lista del servidor incluye categorías que quizá no
   * aparezcan en esta página de resultados, y los chips deben ser los
   * mismos entre una carga y otra.
   */
  const categories = useMemo(
    () => (data?.categories?.length ? data.categories : categoriesOf(products)),
    [data, products],
  );

  const visible = useMemo(
    () => sortProducts(filterByCategory(products, category), sort, currency),
    [products, category, sort, currency],
  );

  return (
    /*
     * El carrito se monta AQUÍ y no en `app/providers.tsx`: es de la
     * tienda, y la raíz lo pondría también en el panel de administración
     * y en las pantallas de acceso, que no lo necesitan. `ShopLayout` es
     * quien lo conecta con el contador de la barra superior y quien pinta
     * el panel lateral.
     */
    <CartProvider>
      <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
        <ShopHero />

        {/*
          El destino de la flecha de la portada.

          `tabIndex={-1}` no lo mete en el orden de tabulación: sirve para
          que `ShopHeroScrollCue` pueda MOVER el foco aquí tras bajar.
          Desplazar la ventana no mueve el foco del teclado, así que sin
          esto quien pulsa la flecha con Intro ve los productos pero su
          siguiente tabulador vuelve arriba, a la portada.

          `scroll-mt-24` reserva el alto de la barra superior, que es
          pegajosa: sin ese margen, el desplazamiento deja los primeros
          filtros escondidos justo debajo de ella. Y `focus:outline-none`
          porque el foco aquí es un apaño de navegación, no un control:
          un anillo alrededor de media pantalla asusta y no informa.
        */}
        <section
          id={SHOP_PRODUCTS_ID}
          tabIndex={-1}
          className="scroll-mt-24 pb-16 pt-14 focus:outline-none sm:pb-20 sm:pt-16"
        >
          <div className="mx-auto grid max-w-6xl gap-8 px-6 sm:px-8">
            <ShopFilters
              categories={categories}
              selectedCategory={category}
              onSelectCategory={setCategory}
              sort={sort}
              onSortChange={setSort}
              count={visible.length}
            />

            {isPending ? (
              <ProductGridSkeleton />
            ) : isError ? (
              <ShopError
                error={error}
                onRetry={() => void refetch()}
                retrying={isFetching}
              />
            ) : visible.length === 0 ? (
              <ShopEmpty category={category} onClearCategory={() => setCategory(null)} />
            ) : (
              <ProductGrid products={visible} currency={currency} locale={locale} />
            )}
          </div>
        </section>

        {/*
          UN solo cierre, y lleva a la franquicia. Llegó a haber dos —uno
          a crear cuenta y otro a franquicias—, pero al mover el primero
          a `/franquicias` los dos apuntaban al mismo sitio: dos remates
          seguidos que dicen lo mismo compiten entre sí y no se pulsa
          ninguno. El mensaje del segundo vive ahora dentro de `ShopCta`.
        */}
        <ShopCta />

        <ContactSection />
      </ShopLayout>
    </CartProvider>
  );
}
